import {
  Basket,
  CalendarDots,
  Camera,
  GraduationCap,
  HandPointing,
  Info,
  Microphone,
  PencilSimple,
  Star,
  type Icon as PhosphorIcon,
} from '@phosphor-icons/react';
import { ChoiceGroup, Icon, Text } from '@shh/ui';
import { useState } from 'react';
import { updateHousehold } from '../../data/api';
import { useFamily } from '../../data/family';
import { settingsText } from '../../i18n/settings';

const ICONS: PhosphorIcon[] = [
  Microphone,
  CalendarDots,
  GraduationCap,
  Camera,
  PencilSimple,
  Star,
  Basket,
  HandPointing,
];
const BENEFITS = settingsText.aiBenefits.map((b, i) => ({ ...b, icon: ICONS[i]! }));

/**
 * Settings → KI-Helfer (Mike, 02.10.): switchable, explained – the wonderful part first, then
 * honestly what is sent where, with the recommendation to leave out sensitive information.
 * Data use per the Gemini API terms (checked 05.10.): we are a provider in the EEA, so the
 * paid-tier rules apply to the free tier as well – no training, no human review, short abuse
 * logs; processing not necessarily in the EU.
 */
export function AiSettings() {
  const f = useFamily();
  const enabled = f.household?.ai?.enabled === true;
  const [error, setError] = useState(false);
  return (
    <div className="flex flex-col gap-section">
      <div className="flex flex-col gap-gap-md">
        <p className="font-display text-xl font-black">{settingsText.aiTitle}</p>
        <ul className="grid gap-gap-md sm:grid-cols-2">
          {BENEFITS.map((b) => (
            <li key={b.title} className="flex items-start gap-gap-md">
              <span className="grid size-avatar-md shrink-0 place-items-center rounded-control bg-accent-primary-soft text-accent-primary">
                <Icon icon={b.icon} size="md" weight="duotone" />
              </span>
              <span className="flex flex-col">
                <span className="font-semibold">{b.title}</span>
                <span className="text-sm text-text-secondary">{b.text}</span>
              </span>
            </li>
          ))}
        </ul>
        <Text size="sm" tone="secondary">
          {settingsText.aiHow}
        </Text>
      </div>

      <ChoiceGroup<'on' | 'off'>
        label={settingsText.aiLabel}
        description={settingsText.aiHint}
        value={enabled ? 'on' : 'off'}
        onChange={(v) => {
          setError(false);
          void updateHousehold(f.householdId, { ai: { enabled: v === 'on' } }).catch(() =>
            setError(true),
          );
        }}
        choices={[
          { value: 'on', label: settingsText.on },
          { value: 'off', label: settingsText.off },
        ]}
      />
      {error && (
        <p role="alert" className="font-semibold text-feedback-danger">
          {settingsText.saveError}
        </p>
      )}

      <section
        aria-labelledby="ki-wissen"
        className="flex flex-col gap-gap-sm rounded-card bg-surface-sunken p-inset-md text-sm"
      >
        <h3 id="ki-wissen" className="flex items-center gap-gap-sm font-semibold">
          <Icon icon={Info} size="sm" /> {settingsText.goodToKnow}
        </h3>
        <p>{settingsText.aiData}</p>
        <p>
          {settingsText.aiGoogle} <strong>{settingsText.aiAdvice}</strong> {settingsText.aiEnough}
        </p>
        <p>{settingsText.aiSpeech}</p>
      </section>
    </div>
  );
}
